// v10/src/components/SuperAdminPanel.js
// CAMBIOS:
// 1. Tab "Configuración" — gestión de precios de planes desde UI
// 2. PriceCard definido FUERA de TabConfig para evitar remount en cada keystroke
// 3. i18n completo con react-i18next

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, LogOut, Plus, Edit2, Trash2, Check, X,
  AlertCircle, Loader, Users, Plane, Activity, Globe,
  ToggleLeft, ToggleRight, Moon, Sun, BarChart2,
  CreditCard, Headphones, Flag, Building2, TrendingUp,
  DollarSign, CheckCircle, Clock, UserPlus, Bell, Settings, Mic, PackageOpen, ShieldCheck, MapPin,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/apiService';
import TabMaintenance from './TabMaintenance';
import GeneralConfirmModal from './common/ConfirmModal';
import { PLAN_FEATURES_MATRIX } from '../utils/planFeaturesMatrix';

const PLANS = [
  { value: 'trial',      label: 'Trial',      color: '#f59e0b', price: 0   },
  { value: 'basic',      label: 'Basic',      color: '#64748b', price: 30  },
  { value: 'pro',        label: 'Pro',        color: '#2563eb', price: 80  },
  { value: 'enterprise', label: 'Enterprise', color: '#7c3aed', price: 120 },
];
const planInfo = (plan) => PLANS.find(p => p.value === plan) || PLANS[0];

// Features agrupadas por categoría para mejor UX en el panel.
// Cada grupo se renderiza con un header divisor.
const FEATURE_LIST = [
  // ── Gestión administrativa ────────────────────────────
  { key: 'flight_log',          label: 'Libro de vuelos digital',   desc: 'Export Excel/CSV/PDF con historial completo de vuelos',  emoji: '📒', group: 'admin' },
  { key: 'suppliers',           label: 'Proveedores de repuestos',  desc: 'Directorio de contactos técnicos',                       emoji: '🔩', group: 'admin' },
  { key: 'qr_access',           label: 'Acceso QR por aeronave',    desc: 'QR directo al selector de aeronaves',                    emoji: '📱', group: 'admin' },
  { key: 'audit_log',           label: 'Audit log inmutable',       desc: 'Registro inmutable de cambios para trazabilidad',        emoji: '🔐', group: 'admin' },
  // ── Checklist ────────────────────────────────────────
  { key: 'custom_checklists',   label: 'Personalización checklist', desc: 'Checklist personalizado por aeronave',                   emoji: '📝', group: 'checklist' },
  { key: 'checklist_versioning',label: 'Versionado de checklists',  desc: 'Historial de versiones aprobadas',                       emoji: '📚', group: 'checklist' },
  { key: 'advanced_voice',      label: 'Voz avanzada (ElevenLabs)', desc: 'TTS de alta calidad con Valentina premium',              emoji: '🎙️', group: 'checklist' },
  { key: 'digital_signature',   label: 'Firma digital',             desc: 'Firma táctil en PDF del chequeo',                        emoji: '✍️', group: 'checklist' },
  { key: 'quick_log_enabled',   label: 'Registro rápido',           desc: 'Registrar vuelos sin checklist obligatorio (bitácora / control de horas)', emoji: '⏱️', group: 'checklist' },
  // ── Reportes ─────────────────────────────────────────
  { key: 'internal_audit_reports', label: 'Reportes auditorías',    desc: 'Reportes para auditorías internas del aeroclub',         emoji: '📊', group: 'reports' },
  // ── Soporte ──────────────────────────────────────────
  { key: 'priority_support',    label: 'Soporte prioritario',       desc: 'Soporte priority + onboarding personalizado',            emoji: '💬', group: 'support' },
  { key: 'sla',                 label: 'SLA + soporte dedicado',    desc: 'SLA contractual y soporte dedicado (Enterprise)',        emoji: '🎯', group: 'support' },
  // ── Performance avanzada (Enterprise) ─────────────────
  { key: 'flow_mode',           label: 'Flow Mode',                 desc: 'Checklist de flujo para pilotos avanzados · Ítems trampa configurables', emoji: '🌀', group: 'enterprise' },
  { key: 'wb',                  label: 'Peso y Balance (W&B)',      desc: 'Cálculo de peso y balance (en validación profesional)',  emoji: '⚖️', group: 'enterprise' },
  { key: 'navigation',          label: 'Planificación Navegación',  desc: 'Planificación de rutas y waypoints (validación)',        emoji: '🗺️', group: 'enterprise' },
  { key: 'pre_landing',         label: 'Checklist pre-aterrizaje',  desc: 'Avance automático por tiempo · Por aeronave',            emoji: '🛬', group: 'enterprise' },
  { key: 'emergency_trainer',   label: 'Emergency Trainer',         desc: 'Simulador de procedimientos de emergencia cronometrado', emoji: '🚨', group: 'enterprise' },
  { key: 'icao_english',        label: 'ICAO English Trainer',      desc: 'Práctica de fraseología ICAO en inglés',                 emoji: '🗣️', group: 'enterprise' },
  { key: 'anac_export',         label: 'Export ANAC',               desc: 'Export específico compatible con auditorías ANAC',       emoji: '📋', group: 'enterprise' },
];

const FEATURE_GROUPS = {
  admin:      { label: 'Gestión administrativa',          color: '#2563eb' },
  checklist:  { label: 'Checklist y operación',           color: '#0891b2' },
  reports:    { label: 'Reportes',                        color: '#16a34a' },
  support:    { label: 'Soporte',                         color: '#64748b' },
  enterprise: { label: '⚠️ Performance avanzada (Enterprise)', color: '#f0a500' },
};

const NAV_ITEMS = [
  { id: 'orgs',        label: 'Organizaciones', icon: Building2,   color: '#7c3aed' },
  { id: 'pending',     label: 'Pendientes',     icon: Clock,       color: '#ef4444' },
  { id: 'metrics',     label: 'Métricas',       icon: BarChart2,   color: '#2563eb' },
  { id: 'billing',     label: 'Facturación',    icon: CreditCard,  color: '#16a34a' },
  { id: 'support',     label: 'Soporte',        icon: Headphones,  color: '#0891b2' },
  { id: 'flags',       label: 'Feature Flags',  icon: Flag,        color: '#f59e0b' },
  { id: 'providers',   label: 'Proveedores',    icon: PackageOpen, color: '#64748b' },
  { id: 'maintenance', label: 'Mantenimiento',  icon: Bell,        color: '#dc2626' },
  { id: 'config',      label: 'Configuración',  icon: Settings,    color: '#475569' },
  { id: 'elevenlabs',  label: 'IA & APIs',      icon: Mic,         color: '#8b5cf6' },
];

const inp = (darkMode) =>
  `w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all text-sm ${
    darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'
  }`;

const card = (darkMode) =>
  `rounded-2xl border transition-all ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;

const StatCard = ({ icon: Icon, label, value, color, sub, darkMode }) => (
  <div className={`${card(darkMode)} p-4 flex items-center gap-3`} style={{ minHeight: 88 }}>
    <div style={{ width:44, height:44, borderRadius:14, background:color+'20', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
      <Icon style={{ width:20, height:20, color }} />
    </div>
    <div>
      <p style={{ margin:0, fontSize:24, fontWeight:900, color: darkMode?'white':'#0f172a', lineHeight:1 }}>{value}</p>
      <p style={{ margin:'2px 0 0', fontSize:12, color: darkMode?'rgba(255,255,255,0.4)':'#64748b' }}>{label}</p>
      <p style={{ margin:'1px 0 0', fontSize:11, color: darkMode?'rgba(255,255,255,0.25)':'#94a3b8', minHeight:14 }}>{sub || ''}</p>
    </div>
  </div>
);

// ── OrgModal ──────────────────────────────────────────────────────
const OrgModal = ({ org, onSave, onClose, darkMode }) => {
  const { t } = useTranslation('admin');
  const isEdit = !!org?.id;
  const [form, setForm] = useState({
    name: org?.name || '', slug: org?.slug || '',
    icao_code: org?.icao_code || '', plan: org?.plan || 'basic',
    owner_email: org?.owner_email || '', monthly_price: org?.monthly_price || '',
    subscription_start: org?.subscription_start?.split('T')[0] || '',
    subscription_end:   org?.subscription_end?.split('T')[0]   || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await onSave(org?.id || null, form, isEdit); onClose(); }
    catch (err) { setError(err.response?.data?.message || 'Error al guardar'); }
    finally { setLoading(false); }
  };

  const i = inp(darkMode);
  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}>
      <motion.div initial={{ scale:0.9, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.9, y:20 }}
        className={`w-full max-w-lg rounded-3xl shadow-2xl p-6 my-4 ${darkMode?'bg-gray-800':'bg-white'}`}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className={`text-xl font-bold ${darkMode?'text-white':'text-gray-900'}`}>
            {isEdit ? t('super_edit_org_title') : t('super_create_org_title')}
          </h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Nombre *</label>
            <input required value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} className={i} placeholder="Aeroclub Puerto Madryn" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Slug *</label>
              <input required value={form.slug} onChange={e=>setForm(f=>({...f,slug:e.target.value.toLowerCase().replace(/\s+/g,'-')}))} className={i} placeholder="puerto-madryn" />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>ICAO</label>
              <input value={form.icao_code} onChange={e=>setForm(f=>({...f,icao_code:e.target.value.toUpperCase()}))} className={i} placeholder="SAVY" maxLength={10} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Plan</label>
              <select value={form.plan} onChange={e=>setForm(f=>({...f,plan:e.target.value}))} className={i}>
                {PLANS.map(p => (
                  <option key={p.value} value={p.value} disabled={p.value === 'enterprise'}>
                    {p.label} — ${p.price}/mes{p.value === 'enterprise' ? ' (Próximamente)' : ''}
                  </option>
                ))}
              </select>
              {isEdit && form.plan !== (org?.plan || 'basic') && (
                <p style={{ margin:'6px 0 0', fontSize:11, color:'#0891b2', fontWeight:600 }}>
                  Al cambiar el plan se actualizarán los feature flags automáticamente
                </p>
              )}
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Precio/mes (USD)</label>
              <input type="number" value={form.monthly_price} onChange={e=>setForm(f=>({...f,monthly_price:e.target.value}))} className={i} placeholder="80" />
            </div>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Email del admin</label>
            <input type="email" value={form.owner_email} onChange={e=>setForm(f=>({...f,owner_email:e.target.value}))} className={i} placeholder="admin@aeroclub.com" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Inicio suscripción</label>
              <input type="date" value={form.subscription_start} onChange={e=>setForm(f=>({...f,subscription_start:e.target.value}))} className={i} />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Vencimiento</label>
              <input type="date" value={form.subscription_end} onChange={e=>setForm(f=>({...f,subscription_end:e.target.value}))} className={i} />
            </div>
          </div>
          {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={loading}
              className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-bold flex items-center justify-center gap-2">
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {isEdit ? t('super_save_org') : t('super_create_org_btn')}
            </button>
            <button type="button" onClick={onClose}
              className={`px-4 py-3 rounded-xl font-bold ${darkMode?'bg-gray-700 text-white':'bg-gray-100 text-gray-700'}`}>
              {t('cancel', { ns:'common' })}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// ── CreateAdminModal ──────────────────────────────────────────────
const ROLES = [
  { value: 'admin',      label: 'Admin',      desc: 'Gestiona flota, usuarios y reportes',  emoji: '🛡️' },
  { value: 'instructor', label: 'Instructor',  desc: 'Autoriza vuelos y cierra squawks',      emoji: '👨‍✈️' },
  { value: 'pilot',      label: 'Piloto',      desc: 'Realiza chequeos prevuelo',             emoji: '🧑‍✈️' },
];

const CreateAdminModal = ({ org, onClose, darkMode }) => {
  const { t } = useTranslation('admin');
  const [form, setForm]       = useState({ name: '', email: '', password: '', role: 'admin' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [done, setDone]       = useState(false);

  const i = `w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all text-sm ${
    darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'
  }`;

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      await api.post(`/api/organizations/${org.id}/admin`, form);
      setDone(true);
      setTimeout(onClose, 1800);
    }
    catch (err) { setError(err.response?.data?.message || 'Error al crear usuario'); }
    finally { setLoading(false); }
  };

  const selectedRole = ROLES.find(r => r.value === form.role) || ROLES[0];

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}>
      <motion.div initial={{ scale:0.9, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.9, y:20 }}
        className={`w-full max-w-md rounded-3xl shadow-2xl p-6 ${darkMode?'bg-gray-800':'bg-white'}`}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className={`text-xl font-bold ${darkMode?'text-white':'text-gray-900'}`}>{t('super_create_user_title')}</h3>
            <p className={`text-sm ${darkMode?'text-gray-400':'text-gray-500'}`}>{org?.name}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <motion.div initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }} className="text-center py-6">
            <div style={{ fontSize:48, marginBottom:12 }}>{selectedRole.emoji}</div>
            <p className={`font-bold text-lg ${darkMode?'text-white':'text-gray-900'}`}>{selectedRole.label} creado</p>
            <p className={`text-sm mt-1 ${darkMode?'text-gray-400':'text-gray-500'}`}>{form.email}</p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode?'text-gray-300':'text-gray-700'}`}>Rol *</label>
              <div style={{ display:'flex', gap:8 }}>
                {ROLES.map(role => (
                  <button key={role.value} type="button"
                    onClick={() => setForm(f => ({ ...f, role: role.value }))}
                    style={{ flex:1, padding:'10px 8px', borderRadius:12, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, textAlign:'center', background: form.role === role.value ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9', color: form.role === role.value ? 'white' : darkMode ? 'rgba(255,255,255,0.5)' : '#64748b', transition: 'all 0.15s' }}>
                    <div style={{ fontSize:18, marginBottom:2 }}>{role.emoji}</div>
                    {role.label}
                  </button>
                ))}
              </div>
              <p style={{ margin:'6px 0 0', fontSize:11, color: darkMode?'rgba(255,255,255,0.35)':'#94a3b8' }}>{selectedRole.desc}</p>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Nombre completo *</label>
              <input required value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} className={i} placeholder="Juan Pérez" />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Email *</label>
              <input required type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={i} placeholder="usuario@aeroclub.com" /> 
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode?'text-gray-300':'text-gray-700'}`}>Contraseña *</label>
              <input required type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} className={i} placeholder="Mínimo 8 caracteres" minLength={8} />
            </div>
            {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={loading}
                className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-bold flex items-center justify-center gap-2">
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Crear {selectedRole.label}
              </button>
              <button type="button" onClick={onClose}
                className={`px-4 py-3 rounded-xl font-bold ${darkMode?'bg-gray-700 text-white':'bg-gray-100 text-gray-700'}`}>
                {t('cancel', { ns:'common' })}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
};

// ── Tab: Organizaciones ───────────────────────────────────────────
const TabOrgs = ({ orgs, loading, error, setError, onEdit, onToggle, onDelete, onNew, onCreateAdmin, onImpersonate, darkMode }) => {
  const { t } = useTranslation('admin');
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const sub  = darkMode ? 'text-gray-400' : 'text-gray-500';
  const [showInactive, setShowInactive] = React.useState(false);
  const visibleOrgs = showInactive ? orgs : orgs.filter(o => o.is_active);
  const stats = {
    total_orgs:    orgs.length,
    active_orgs:   orgs.filter(o => o.is_active).length,
    inactive_orgs: orgs.filter(o => !o.is_active).length,
    total_pilots:  orgs.reduce((a, o) => a + (parseInt(o.pilot_count)  || 0), 0),
    total_flights: orgs.reduce((a, o) => a + (parseInt(o.flight_count) || 0), 0),
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-2">
        <StatCard icon={Building2}   label={t('stat_total_orgs')}  value={stats.total_orgs}    color="#7c3aed" sub={`${stats.active_orgs} ${t('stat_active').toLowerCase()}`} darkMode={darkMode} />
        <StatCard icon={CheckCircle} label={t('stat_active')}      value={stats.active_orgs}   color="#16a34a" darkMode={darkMode} />
        <StatCard icon={Globe}       label={t('stat_inactive')}    value={stats.inactive_orgs} color="#64748b" sub={stats.inactive_orgs > 0 ? t('super_inactive_badge') : 'Sin inactivas'} darkMode={darkMode} />
        <StatCard icon={Users}       label={t('stat_pilots')}      value={stats.total_pilots}  color="#2563eb" darkMode={darkMode} />
        <StatCard icon={Plane}       label={t('stat_flights')}     value={stats.total_flights} color="#0891b2" darkMode={darkMode} />
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className={`text-2xl font-black ${text}`}>{t('nav_orgs')}</h2>
          <p className={`text-sm mt-1 ${sub}`}>{t('super_orgs_subtitle', { count: visibleOrgs.length, total: orgs.length })}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {orgs.some(o => !o.is_active) && (
            <motion.button onClick={() => setShowInactive(v => !v)} whileTap={{ scale:0.97 }}
              style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 14px', borderRadius:14, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700,
                background: showInactive ? (darkMode?'rgba(245,158,11,0.15)':'rgba(245,158,11,0.1)') : (darkMode?'rgba(255,255,255,0.06)':'#f1f5f9'),
                color: showInactive ? '#d97706' : (darkMode?'rgba(255,255,255,0.5)':'#64748b'),
                border: showInactive ? '1px solid rgba(245,158,11,0.3)' : '1px solid transparent' }}>
              <span style={{ fontSize:15 }}>👁</span>
              {showInactive ? t('super_hide_inactive') : t('super_show_inactive', { count: orgs.filter(o => !o.is_active).length })}
            </motion.button>
          )}
          <motion.button onClick={onNew} whileHover={{ scale:1.03 }} whileTap={{ scale:0.97 }}
            className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-2xl font-bold shadow-lg">
            <Plus className="w-5 h-5" /> {t('super_new_org')}
          </motion.button>
        </div>
      </div>
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />{error}
          <button onClick={()=>setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader className="w-8 h-8 animate-spin text-purple-500" /></div>
      ) : orgs.length === 0 ? (
        <div className={`${card(darkMode)} text-center py-16`}>
          <Globe className={`w-12 h-12 mx-auto mb-3 ${sub}`} />
          <p className={`font-medium ${sub}`}>{t('super_no_orgs')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleOrgs.map((org, idx) => {
            const plan = planInfo(org.plan);
            const isExpiringSoon = org.subscription_end &&
              new Date(org.subscription_end) < new Date(Date.now() + 7*24*60*60*1000);
            return (
              <motion.div key={org.id} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} transition={{ delay:idx*0.04 }}
                className={`${card(darkMode)} p-4`}>
                <div className="flex items-center gap-4">
                  <div style={{ width:48, height:48, borderRadius:14, background:org.is_active?'linear-gradient(135deg,#7c3aed,#4f46e5)':'rgba(100,116,139,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:22 }}>✈️</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-bold text-base ${text} ${!org.is_active?'opacity-50':''}`}>{org.name}</p>
                      {org.icao_code && <span className={`text-xs px-2 py-0.5 rounded-full font-mono font-bold ${darkMode?'bg-gray-700 text-gray-300':'bg-gray-100 text-gray-600'}`}>{org.icao_code}</span>}
                      <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background:plan.color+'15', color:plan.color }}>{plan.label}</span>
                      {!org.is_active && <span className={`text-xs px-2 py-0.5 rounded-full border ${darkMode?'bg-gray-700 text-gray-400 border-gray-600':'bg-gray-100 text-gray-500 border-gray-200'}`}>{t('super_inactive_badge')}</span>}
                      {isExpiringSoon && org.is_active && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">{t('super_expires_soon')}</span>}
                    </div>
                    <div className={`flex gap-4 text-xs mt-1 ${sub} flex-wrap`}>
                      <span>🔗 {org.slug}</span>
                      {org.owner_email  && <span>📧 {org.owner_email}</span>}
                      {org.pilot_count  > 0 && <span>👤 {org.pilot_count} pilotos</span>}
                      {org.flight_count > 0 && <span>✈️ {org.flight_count} vuelos</span>}
                      {org.monthly_price > 0 && <span className="text-green-600 font-bold">💵 ${org.monthly_price}/mes</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={()=>onCreateAdmin(org)} title={t('super_create_user_title')}
                      className={`p-2 rounded-xl transition-all ${darkMode?'hover:bg-gray-700 text-gray-400 hover:text-purple-400':'hover:bg-purple-50 text-gray-500 hover:text-purple-600'}`}>
                      <UserPlus className="w-4 h-4" />
                    </button>
                    <button onClick={()=>onImpersonate(org)} title={t('super_see_panel')}
                      className={`p-2 rounded-xl transition-all ${darkMode?'hover:bg-gray-700 text-gray-400 hover:text-cyan-400':'hover:bg-cyan-50 text-gray-500 hover:text-cyan-600'}`}>
                      <Headphones className="w-4 h-4" />
                    </button>
                    <button onClick={()=>onEdit(org)} className={`p-2 rounded-xl transition-all ${darkMode?'hover:bg-gray-700 text-gray-400 hover:text-blue-400':'hover:bg-blue-50 text-gray-500 hover:text-blue-600'}`}><Edit2 className="w-4 h-4" /></button>
                    <button onClick={()=>onToggle(org)} className={`p-2 rounded-xl transition-all ${org.is_active?(darkMode?'hover:bg-gray-700 text-gray-400 hover:text-amber-400':'hover:bg-amber-50 text-gray-500 hover:text-amber-500'):(darkMode?'hover:bg-gray-700 text-gray-400 hover:text-green-400':'hover:bg-green-50 text-gray-500 hover:text-green-500')}`}>
                      {org.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                    </button>
                    <button onClick={()=>onDelete(org)} className={`p-2 rounded-xl transition-all ${darkMode?'hover:bg-gray-700 text-gray-400 hover:text-red-400':'hover:bg-red-50 text-gray-500 hover:text-red-500'}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Tab: Métricas ─────────────────────────────────────────────────
const TabMetrics = ({ darkMode }) => {
  const { t } = useTranslation('admin');
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const sub  = darkMode ? 'text-gray-400' : 'text-gray-500';
  useEffect(() => {
    api.get('/api/organizations/stats')
      .then(r => setStats(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="flex items-center justify-center py-16"><Loader className="w-8 h-8 animate-spin text-purple-500" /></div>;
  if (!stats)  return <p className={sub}>Error al cargar métricas</p>;
  return (
    <div className="space-y-6">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <h2 className={`text-2xl font-black ${text}`}>{t('super_metrics_title')}</h2>
          <p className={`text-sm mt-1 ${sub}`}>{t('super_metrics_desc')}</p>
        </div>
        <span style={{ fontSize:12, fontWeight:700, padding:'6px 14px', borderRadius:20, background: darkMode?'rgba(124,58,237,0.15)':'rgba(124,58,237,0.08)', color:'#7c3aed', border:'1px solid rgba(124,58,237,0.2)' }}>
          {t('super_metrics_realtime')}
        </span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Building2} label={t('stat_global_orgs')}    value={stats.total_orgs}       color="#7c3aed" sub={`${stats.active_orgs} ${t('stat_active').toLowerCase()}`}                       darkMode={darkMode} />
        <StatCard icon={Users}     label={t('stat_total_pilots')}   value={stats.total_pilots}      color="#2563eb" sub={`${stats.total_instructors} instructores`}                                        darkMode={darkMode} />
        <StatCard icon={Plane}     label={t('stat_aircraft')}       value={stats.total_aircraft}    color="#0891b2" darkMode={darkMode} />
        <StatCard icon={Activity}  label={t('stat_inspections')}    value={stats.total_inspections} color="#16a34a" sub={`${stats.inspections_last_30d} este mes`}                                         darkMode={darkMode} />
      </div>
      <div className={`${card(darkMode)} p-5`}>
        <h3 className={`font-bold text-base mb-4 ${text}`}>{t('super_top_orgs')}</h3>
        {stats.top_orgs?.length === 0 ? (
          <p className={`text-sm ${sub}`}>{t('super_no_activity')}</p>
        ) : (
          <div className="space-y-3">
            {stats.top_orgs.map((org, i) => {
              const max  = stats.top_orgs[0]?.inspection_count || 1;
              const pct  = Math.round((org.inspection_count / max) * 100);
              const plan = planInfo(org.plan);
              return (
                <div key={org.id}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${text}`}>{org.name}</span>
                      <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:20, background:plan.color+'15', color:plan.color }}>{plan.label}</span>
                    </div>
                    <span className={`text-sm font-bold ${text}`}>{org.inspection_count} chequeos</span>
                  </div>
                  <div style={{ height:7, borderRadius:4, background: darkMode?'rgba(255,255,255,0.06)':'#f1f5f9', overflow:'hidden' }}>
                    <motion.div initial={{ width:0 }} animate={{ width:`${pct}%` }} transition={{ duration:0.7, delay:i*0.1, ease:'easeOut' }}
                      style={{ height:'100%', borderRadius:4, background: org.plan === 'pro' ? 'linear-gradient(90deg,#2563eb,#7c3aed)' : org.plan === 'enterprise' ? 'linear-gradient(90deg,#7c3aed,#a21caf)' : org.plan === 'basic' ? 'linear-gradient(90deg,#0891b2,#2563eb)' : 'linear-gradient(90deg,#64748b,#94a3b8)' }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className={`${card(darkMode)} p-5`} style={{ background: darkMode?'rgba(22,163,74,0.06)':'rgba(22,163,74,0.04)', border: darkMode?'1px solid rgba(22,163,74,0.2)':'1px solid rgba(22,163,74,0.15)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
          <h3 className={`font-bold text-base ${text}`}>{t('super_mrr_label')}</h3>
          <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20, background:'rgba(22,163,74,0.12)', color:'#16a34a' }}>💵 {t('super_mrr_sub')}</span>
        </div>
        <p style={{ margin:'0 0 4px', fontSize:40, fontWeight:900, color:'#16a34a', letterSpacing:'-1px', lineHeight:1 }}>
          ${parseFloat(stats.mrr||0).toLocaleString('es-AR', { minimumFractionDigits:0, maximumFractionDigits:0 })}
          <span style={{ fontSize:18, fontWeight:600, color:'rgba(22,163,74,0.6)', marginLeft:4 }}>USD/mes</span>
        </p>
        <p className={`text-sm mt-1 ${sub}`}>Suma de precios configurados en {stats.active_orgs} org{stats.active_orgs !== 1 ? 's' : ''} activa{stats.active_orgs !== 1 ? 's' : ''}</p>
      </div>
    </div>
  );
};

// ── Tab: Facturación ──────────────────────────────────────────────
const TabBilling = ({ orgs, darkMode }) => {
  const { t } = useTranslation('admin');
  const text  = darkMode ? 'text-white' : 'text-gray-900';
  const sub   = darkMode ? 'text-gray-400' : 'text-gray-500';
  const mrr   = orgs.filter(o=>o.is_active).reduce((a,o)=>a+parseFloat(o.monthly_price||0),0);
  const today = new Date();
  const getStatus = (org) => {
    if (!org.is_active) return { label: t('bill_inactive'), color:'#64748b' };
    if (!org.subscription_end) return { label: t('bill_no_expiry'), color:'#0891b2' };
    const end  = new Date(org.subscription_end);
    const days = Math.ceil((end - today) / (1000*60*60*24));
    if (days < 0) return { label: t('bill_expired'), color:'#dc2626' };
    if (days < 7) return { label: t('bill_expires_in', { days }), color:'#d97706' };
    return { label: t('bill_ok'), color:'#16a34a' };
  };
  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-2xl font-black ${text}`}>{t('super_billing_title')}</h2>
        <p className={`text-sm mt-1 ${sub}`}>{t('super_billing_desc')}</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <StatCard icon={DollarSign}  label={t('stat_mrr')}       value={`$${mrr.toFixed(0)}`} color="#16a34a" sub="USD / mes" darkMode={darkMode} />
        <StatCard icon={CheckCircle} label={t('stat_on_time')}   value={orgs.filter(o => getStatus(o).label === t('bill_ok')).length} color="#2563eb" darkMode={darkMode} />
        <StatCard icon={Clock}       label={t('stat_expiring')}  value={orgs.filter(o => { const s = getStatus(o); return s.label.includes('Vence') || s.label === t('bill_expired'); }).length} color="#d97706" darkMode={darkMode} />
      </div>
      <div className={`${card(darkMode)} overflow-hidden`}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background: darkMode?'rgba(255,255,255,0.04)':'#f8fafc' }}>
              {[t('bill_org'), t('bill_plan'), t('bill_price_month'), t('bill_expiry'), t('bill_status')].map(h => (
                <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:11, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.06em', color: darkMode?'rgba(255,255,255,0.35)':'#94a3b8' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orgs.map((org, i) => {
              const plan   = planInfo(org.plan);
              const status = getStatus(org);
              return (
                <tr key={org.id} style={{ borderTop: darkMode?'1px solid rgba(255,255,255,0.05)':'1px solid #f1f5f9', background:i%2===0?(darkMode?'rgba(255,255,255,0.01)':'rgba(0,0,0,0.01)'):'transparent' }}>
                  <td style={{ padding:'14px 16px' }}>
                    <p style={{ margin:0, fontWeight:700, fontSize:13, color:darkMode?'white':'#0f172a' }}>{org.name}</p>
                    {org.owner_email && <p style={{ margin:'2px 0 0', fontSize:11, color:darkMode?'rgba(255,255,255,0.3)':'#94a3b8' }}>{org.owner_email}</p>}
                  </td>
                  <td style={{ padding:'14px 16px' }}><span style={{ fontSize:12, fontWeight:700, padding:'3px 10px', borderRadius:20, background:plan.color+'15', color:plan.color }}>{plan.label}</span></td>
                  <td style={{ padding:'14px 16px', fontWeight:700, fontSize:14, color:'#16a34a' }}>{org.monthly_price > 0 ? `$${org.monthly_price}` : <span style={{ color:darkMode?'rgba(255,255,255,0.3)':'#94a3b8', fontWeight:400 }}>—</span>}</td>
                  <td style={{ padding:'14px 16px', fontSize:13, color:darkMode?'rgba(255,255,255,0.5)':'#64748b' }}>{org.subscription_end ? new Date(org.subscription_end).toLocaleDateString('es-AR') : '—'}</td>
                  <td style={{ padding:'14px 16px' }}><span style={{ fontSize:12, fontWeight:700, padding:'3px 10px', borderRadius:20, background:status.color+'15', color:status.color }}>{status.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Tab: Soporte ──────────────────────────────────────────────────
const TabSupport = ({ orgs, onImpersonate, darkMode }) => {
  const { t } = useTranslation('admin');
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const sub  = darkMode ? 'text-gray-400' : 'text-gray-500';
  const [search, setSearch] = useState('');
  const filtered = orgs.filter(o => !search || o.name.toLowerCase().includes(search.toLowerCase()) || (o.icao_code||'').toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-2xl font-black ${text}`}>{t('super_support_title')}</h2>
        <p className={`text-sm mt-1 ${sub}`}>{t('super_support_desc')}</p>
      </div>
      <div className={`${card(darkMode)} p-3`}>
        <input type="text" placeholder={t('super_support_search')} value={search} onChange={e=>setSearch(e.target.value)}
          className={`w-full px-4 py-2.5 rounded-xl border focus:outline-none focus:ring-2 focus:ring-purple-500 ${darkMode?'bg-gray-700 border-gray-600 text-white':'bg-gray-50 border-gray-200 text-gray-900'}`} />
      </div>
      <div className="space-y-3">
        {filtered.map((org, i) => {
          const plan = planInfo(org.plan);
          return (
            <motion.div key={org.id} initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay:i*0.04 }}
              className={`${card(darkMode)} p-4 flex items-center gap-4`}>
              <div style={{ width:44, height:44, borderRadius:12, background:org.is_active?'linear-gradient(135deg,#7c3aed,#4f46e5)':'rgba(100,116,139,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>✈️</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div className="flex items-center gap-2">
                  <p className={`font-bold text-sm ${text}`}>{org.name}</p>
                  {org.icao_code && <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${darkMode?'bg-gray-700 text-gray-300':'bg-gray-100 text-gray-600'}`}>{org.icao_code}</span>}
                  <span style={{ fontSize:11, fontWeight:700, padding:'1px 8px', borderRadius:20, background:plan.color+'15', color:plan.color }}>{plan.label}</span>
                  {!org.is_active && <span style={{ fontSize:11, padding:'1px 8px', borderRadius:20, background:'rgba(100,116,139,0.1)', color:'#64748b' }}>{t('super_inactive_badge')}</span>}
                </div>
                <div className={`flex gap-3 text-xs mt-1 ${sub}`}>
                  {org.owner_email && <span>📧 {org.owner_email}</span>}
                  {org.pilot_count > 0 && <span>👤 {org.pilot_count} pilotos</span>}
                </div>
              </div>
              <motion.button onClick={() => onImpersonate && onImpersonate(org)}
                whileHover={{ scale:1.03 }} whileTap={{ scale:0.97 }}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:12, border:'none', cursor:'pointer', background:'linear-gradient(135deg,#0891b2,#0e7490)', color:'white', fontWeight:700, fontSize:12, fontFamily:'inherit', flexShrink:0 }}>
                <Headphones style={{ width:14, height:14 }} /> {t('super_see_panel')}
              </motion.button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

// ── Tab: Pendientes ───────────────────────────────────────────────
const TabPending = ({ darkMode, onOrgApproved }) => {
  const { t } = useTranslation('admin');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [orgs, setOrgs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing]   = useState(null);
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const sub  = darkMode ? 'text-gray-400' : 'text-gray-500';
  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/api/organizations/pending'); setOrgs(res.data.data || []); }
    catch { setOrgs([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const handleApprove = async (org) => {
    setActing(org.id + '_approve');
    try { await api.post(`/api/organizations/${org.id}/approve`); await load(); if (onOrgApproved) onOrgApproved(); }
    catch { } finally { setActing(null); }
  };
  const handleRejectClick = (org) => setRejectTarget(org);
  const handleRejectConfirm = async () => {
    const org = rejectTarget;
    if (!org) return;
    setActing(org.id + '_reject');
    try { await api.post(`/api/organizations/${org.id}/reject`); await load(); }
    catch { } finally { setActing(null); setRejectTarget(null); }
  };
  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-2xl font-black ${text}`}>{t('super_pending_title')}</h2>
        <p className={`text-sm mt-1 ${sub}`}>{t('super_pending_desc')}</p>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader className="w-8 h-8 animate-spin text-red-500" /></div>
      ) : orgs.length === 0 ? (
        <div className={`${card(darkMode)} text-center py-16`}>
          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
          <p className={`font-medium ${sub}`}>{t('super_no_pending')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orgs.map((org, i) => (
            <motion.div key={org.id} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} transition={{ delay:i*0.06 }}
              className={`${card(darkMode)} p-5`}>
              <div className="flex items-start gap-4">
                <div style={{ width:48, height:48, borderRadius:14, background:'linear-gradient(135deg,#ef4444,#dc2626)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>🏢</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className={`font-bold text-base ${text}`}>{org.name}</p>
                    {org.icao_code && <span className={`text-xs px-2 py-0.5 rounded-full font-mono font-bold ${darkMode?'bg-gray-700 text-gray-300':'bg-gray-100 text-gray-600'}`}>{org.icao_code}</span>}
                    <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20, background:'rgba(239,68,68,0.1)', color:'#ef4444' }}>Pendiente</span>
                  </div>
                  <div className={`text-xs space-y-1 ${sub}`}>
                    <p>🔗 {org.slug}</p>
                    {org.admin_name  && <p>👤 Admin: {org.admin_name}</p>}
                    {org.admin_email && <p>📧 {org.admin_email}</p>}
                    <p>📅 Solicitado: {new Date(org.created_at).toLocaleDateString('es-AR')}</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <motion.button onClick={() => handleApprove(org)} disabled={!!acting}
                  whileHover={!acting?{ scale:1.02 }:{}} whileTap={!acting?{ scale:0.97 }:{}}
                  style={{ flex:1, padding:'11px', borderRadius:14, border:'none', cursor:acting?'not-allowed':'pointer', fontFamily:'inherit', fontWeight:700, fontSize:14, color:'white', background:'linear-gradient(135deg,#16a34a,#15803d)', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                  {acting === org.id + '_approve' ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  {t('super_approve')}
                </motion.button>
                <motion.button onClick={() => handleRejectClick(org)} disabled={!!acting}
                  whileHover={!acting?{ scale:1.02 }:{}} whileTap={!acting?{ scale:0.97 }:{}}
                  style={{ flex:1, padding:'11px', borderRadius:14, border:'none', cursor:acting?'not-allowed':'pointer', fontFamily:'inherit', fontWeight:700, fontSize:14, color:'white', background:'linear-gradient(135deg,#ef4444,#dc2626)', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                  {acting === org.id + '_reject' ? <Loader className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                  {t('super_reject')}
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
      <GeneralConfirmModal
        isOpen={!!rejectTarget}
        title={t('confirm_modal_org_reject_title')}
        message={t('confirm_modal_org_reject_message', { name: rejectTarget?.name || '' })}
        confirmLabel={t('super_reject')}
        variant="danger"
        isLoading={acting === (rejectTarget?.id + '_reject')}
        onConfirm={handleRejectConfirm}
        onCancel={() => setRejectTarget(null)}
        darkMode={darkMode}
      />
    </div>
  );
};

// ── Tab: Feature Flags ────────────────────────────────────────────
const TabFlags = ({ orgs, onFlagsUpdate, darkMode }) => {
  const { t } = useTranslation('admin');
  const [selectedOrg, setSelectedOrg] = useState(orgs[0] || null);
  const parseFeatures = (f) => {
    if (!f) return {};
    if (typeof f === 'string') { try { return JSON.parse(f); } catch { return {}; } }
    return f;
  };
  const [features, setFeatures] = useState(() => parseFeatures(selectedOrg?.features));
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const sub  = darkMode ? 'text-gray-400' : 'text-gray-500';
  useEffect(() => { if (selectedOrg) setFeatures(parseFeatures(selectedOrg.features)); }, [selectedOrg]);
  const handleToggle = (key) => { setFeatures(f => ({ ...f, [key]: !f[key] })); setSaved(false); };
  const handleSave = async () => {
    if (!selectedOrg) return;
    setSaving(true);
    try {
      await api.put(`/api/organizations/${selectedOrg.id}/features`, { features });
      onFlagsUpdate(selectedOrg.id, features);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch { } finally { setSaving(false); }
  };
  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-2xl font-black ${text}`}>{t('super_flags_title')}</h2>
        <p className={`text-sm mt-1 ${sub}`}>{t('super_flags_desc')}</p>
      </div>
      <div className={`${card(darkMode)} p-3 flex gap-2 flex-wrap`}>
        {orgs.map(org => (
          <button key={org.id} onClick={() => setSelectedOrg(org)}
            style={{ padding:'8px 14px', borderRadius:12, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700,
              background: selectedOrg?.id === org.id ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : darkMode?'rgba(255,255,255,0.06)':'#f1f5f9',
              color: selectedOrg?.id === org.id ? 'white' : darkMode?'rgba(255,255,255,0.5)':'#64748b',
              opacity: org.is_active ? 1 : 0.5 }}>
            {org.name}
            {org.icao_code && <span style={{ marginLeft:6, fontSize:11, opacity:0.7 }}>{org.icao_code}</span>}
          </button>
        ))}
      </div>
      {selectedOrg && (
        <div className={`${card(darkMode)} p-5 space-y-4`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-bold text-base ${text}`}>{selectedOrg.name}</p>
              <p className={`text-xs ${sub}`}>Plan {planInfo(selectedOrg.plan).label}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => {
                  const planMatrix = PLAN_FEATURES_MATRIX[selectedOrg.plan];
                  if (!planMatrix) return;
                  const hasOverrides = features && Object.keys(features).length > 0;
                  if (hasOverrides) {
                    const confirmed = window.confirm(
                      `Esta organización tiene overrides manuales en features.\n\n` +
                      `Aplicar el preset del plan "${selectedOrg.plan}" va a SOBREESCRIBIR` +
                      ` esos overrides con los defaults del plan.\n\n¿Continuar?`
                    );
                    if (!confirmed) return;
                  }
                  setFeatures(planMatrix);
                  setSaved(false);
                }}
                style={{ display:'flex', alignItems:'center', gap:5, padding:'9px 14px', borderRadius:12, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, background: darkMode?'rgba(255,255,255,0.06)':'#f1f5f9', color: darkMode?'rgba(255,255,255,0.5)':'#64748b' }}>
                {t('super_flags_plan_preset')}
              </button>
              <motion.button onClick={handleSave} disabled={saving}
                whileHover={!saving?{ scale:1.03 }:{}} whileTap={!saving?{ scale:0.97 }:{}}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 18px', borderRadius:12, border:'none', cursor:saving?'not-allowed':'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700, background: saved ? '#16a34a' : 'linear-gradient(135deg,#7c3aed,#4f46e5)', color:'white' }}>
                {saving ? <Loader className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : null}
                {saving ? `${t('super_config_saving')}` : saved ? '¡Guardado!' : t('super_save_org')}
              </motion.button>
            </div>
          </div>
          <div className="space-y-3">
            {Object.entries(FEATURE_GROUPS).map(([groupKey, groupInfo]) => {
              const groupFeatures = FEATURE_LIST.filter(f => f.group === groupKey);
              if (groupFeatures.length === 0) return null;
              return (
                <div key={groupKey} style={{ marginBottom: 24 }}>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: groupInfo.color,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    marginBottom: 12,
                    paddingBottom: 6,
                    borderBottom: `1px solid ${groupInfo.color}33`,
                  }}>
                    {groupInfo.label}
                  </div>
                  {groupFeatures.map(feat => {
                    const isOn = !!features[feat.key];
                    return (
                      <div key={feat.key} className="flex items-center justify-between py-3"
                        style={{ borderBottom: darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid #f1f5f9' }}>
                        <div className="flex items-center gap-3">
                          <span style={{ fontSize:22 }}>{feat.emoji}</span>
                          <div>
                            <p style={{ margin:0, fontSize:14, fontWeight:700, color:darkMode?'white':'#0f172a' }}>{feat.label}</p>
                            <p style={{ margin:0, fontSize:12, color:darkMode?'rgba(255,255,255,0.4)':'#64748b' }}>{feat.desc}</p>
                          </div>
                        </div>
                        <motion.button onClick={() => handleToggle(feat.key)} whileTap={{ scale:0.9 }}
                          style={{ flexShrink:0, width:52, height:28, borderRadius:14, border:'none', cursor:'pointer', position:'relative', background: isOn ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : darkMode?'rgba(255,255,255,0.1)':'#e2e8f0', transition:'background 0.2s' }}>
                          <motion.div animate={{ x: isOn ? 26 : 2 }} transition={{ type:'spring', stiffness:300, damping:25 }}
                            style={{ position:'absolute', top:2, width:24, height:24, borderRadius:12, background:'white', boxShadow:'0 1px 4px rgba(0,0,0,0.2)' }} />
                        </motion.button>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Cloudinary Monitor ────────────────────────────────────────────
const CloudinaryMonitor = ({ darkMode }) => {
  const [data,    setData]    = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState(null);
  const text = darkMode ? 'rgba(255,255,255,0.85)' : '#0f172a';
  const sub  = darkMode ? 'rgba(255,255,255,0.4)'  : '#64748b';

  const load = async () => {
    setLoading(true); setError(null);
    try { const res = await api.get('/api/cloudinary/usage'); setData(res.data.data); }
    catch { setError('No se pudo obtener el uso de Cloudinary'); }
    finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);

  const Bar = ({ pct, color }) => (
    <div style={{ height:8, borderRadius:99, background: darkMode?'rgba(255,255,255,0.08)':'#f1f5f9', overflow:'hidden', marginTop:6 }}>
      <div style={{ height:'100%', width:`${pct}%`, borderRadius:99, background:color, transition:'width 0.6s ease' }} />
    </div>
  );
  const barColor = (pct) => pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#22c55e';

  return (
    <div style={{ borderRadius:20, background: darkMode?'rgba(255,255,255,0.04)':'white', border: darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid #e2e8f0' }}>
      <div style={{ padding:'20px 24px', borderBottom: darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:28 }}>☁️</span>
          <div>
            <p style={{ margin:0, fontWeight:800, fontSize:16, color:text }}>Cloudinary</p>
            <p style={{ margin:0, fontSize:12, color:sub }}>Storage de imágenes y audios</p>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {data && <span style={{ fontSize:11, fontWeight:700, color:'#64748b', background:'rgba(100,116,139,0.1)', padding:'3px 10px', borderRadius:99 }}>Plan: {data.plan}</span>}
          <button onClick={load} style={{ padding:'8px 14px', background: darkMode?'rgba(255,255,255,0.06)':'#f1f5f9', border:'none', borderRadius:10, cursor:'pointer', fontSize:12, fontWeight:600, color:sub, fontFamily:'inherit' }}>↻ Actualizar</button>
        </div>
      </div>
      <div style={{ padding:'20px 24px' }}>
        {loading ? (
          <div style={{ display:'flex', alignItems:'center', gap:10, color:sub }}>
            <div style={{ width:18, height:18, borderRadius:'50%', border:'2.5px solid #f97316', borderTopColor:'transparent', animation:'spin 1s linear infinite' }} />
            Consultando Cloudinary...
          </div>
        ) : error ? (
          <div style={{ color:'#ef4444', fontSize:13 }}>⚠️ {error}</div>
        ) : data ? (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {data.credits.limit > 0 && (
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:2 }}>
                  <span style={{ fontSize:13, fontWeight:700, color:text }}>Creditos mensuales</span>
                  <span style={{ fontSize:13, fontWeight:800, color:barColor(data.credits.pct) }}>{data.credits.used} / {data.credits.limit} ({data.credits.pct}%)</span>
                </div>
                <Bar pct={data.credits.pct} color={barColor(data.credits.pct)} />
              </div>
            )}
            {data.storage.limit > 0 && (
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:2 }}>
                  <span style={{ fontSize:13, fontWeight:700, color:text }}>Storage</span>
                  <span style={{ fontSize:13, fontWeight:800, color:barColor(data.storage.pct) }}>{data.storage.usedHuman} / {data.storage.limitHuman} ({data.storage.pct}%)</span>
                </div>
                <Bar pct={data.storage.pct} color={barColor(data.storage.pct)} />
              </div>
            )}
            {data.storage.limit === 0 && data.storage.used > 0 && <p style={{ margin:0, fontSize:13, color:sub }}>💾 Storage usado: <strong style={{ color:text }}>{data.storage.usedHuman}</strong></p>}
            {data.bandwidth.limit === 0 && data.bandwidth.used > 0 && <p style={{ margin:0, fontSize:13, color:sub }}>📡 Bandwidth usado: <strong style={{ color:text }}>{data.bandwidth.usedHuman}</strong></p>}
            <p style={{ margin:0, fontSize:12, color:sub }}>📁 {data.resources?.toLocaleString('es-AR')} recursos almacenados</p>
            <a href="https://console.cloudinary.com" target="_blank" rel="noopener noreferrer"
              style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'10px 16px', borderRadius:12, background: darkMode?'rgba(249,115,22,0.1)':'rgba(249,115,22,0.06)', border:'1px solid rgba(249,115,22,0.25)', color:'#f97316', textDecoration:'none', fontSize:13, fontWeight:700, alignSelf:'flex-start' }}>
              Abrir dashboard Cloudinary
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
};

// ── Tab: ElevenLabs / IA & APIs ───────────────────────────────────
const TabElevenLabs = ({ darkMode }) => {
  const { t } = useTranslation('admin');
  const [data,    setData]    = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { const res = await api.get('/api/tts/usage'); setData(res.data.data); }
    catch { setError('No se pudo obtener el uso de ElevenLabs'); }
    finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);

  const text = darkMode ? 'rgba(255,255,255,0.85)' : '#0f172a';
  const sub  = darkMode ? 'rgba(255,255,255,0.4)'  : '#64748b';

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:60, gap:12 }}>
      <div style={{ width:24, height:24, borderRadius:'50%', border:'3px solid #8b5cf6', borderTopColor:'transparent', animation:'spin 1s linear infinite' }} />
      <p style={{ color:sub }}>Consultando ElevenLabs...</p>
    </div>
  );

  if (error) return (
    <div style={{ padding:32, textAlign:'center', color:'#ef4444' }}>
      <p style={{ fontSize:40, marginBottom:12 }}>⚠️</p>
      <p style={{ fontWeight:700 }}>{error}</p>
      <button onClick={load} style={{ marginTop:16, padding:'10px 20px', background:'#8b5cf6', color:'white', border:'none', borderRadius:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>Reintentar</button>
    </div>
  );

  if (!data) return null;

  const pct         = data.pctUsed || 0;
  const barColor    = data.critical ? '#ef4444' : data.alert ? '#f59e0b' : '#22c55e';
  const statusLabel = data.critical ? t('el_critical') : data.alert ? t('el_alert') : t('el_normal');
  const statusColor = data.critical ? '#ef4444' : data.alert ? '#f59e0b' : '#22c55e';

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      <div>
        <h2 style={{ margin:'0 0 4px', fontSize:20, fontWeight:900, color:text }}>{t('el_title')}</h2>
        <p style={{ margin:0, fontSize:13, color:sub }}>{t('el_desc')}</p>
      </div>

      <div style={{ borderRadius:20, overflow:'hidden', background: darkMode ? 'rgba(255,255,255,0.04)' : 'white', border: data.critical ? '1.5px solid #ef4444' : data.alert ? '1.5px solid #f59e0b' : darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0', boxShadow: data.critical ? '0 0 24px rgba(239,68,68,0.15)' : 'none' }}>
        <div style={{ padding:'20px 24px', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:28 }}>🎙️</span>
            <div>
              <p style={{ margin:0, fontWeight:800, fontSize:16, color:text }}>Valentina — Voz IA</p>
              <p style={{ margin:0, fontSize:12, color:sub }}>Plan: <strong style={{ color:text }}>{data.tier}</strong></p>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:12, fontWeight:700, color:statusColor, background:`${statusColor}18`, padding:'4px 12px', borderRadius:99, border:`1px solid ${statusColor}40` }}>{statusLabel}</span>
            <button onClick={load} style={{ padding:'8px 14px', background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9', border:'none', borderRadius:10, cursor:'pointer', fontSize:12, fontWeight:600, color:sub, fontFamily:'inherit' }}>↻ Actualizar</button>
          </div>
        </div>
        <div style={{ padding:'24px' }}>
          <div style={{ marginBottom:20 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <span style={{ fontSize:13, fontWeight:700, color:text }}>{t('el_chars_used')}</span>
              <span style={{ fontSize:13, fontWeight:800, color:barColor }}>{pct}%</span>
            </div>
            <div style={{ height:12, borderRadius:99, background: darkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9', overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${pct}%`, borderRadius:99, background:barColor, transition:'width 0.6s ease' }} />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
            {[
              { label:'Usados',    value: data.used?.toLocaleString('es-AR'),      color: barColor },
              { label:'Restantes', value: data.remaining?.toLocaleString('es-AR'), color: data.critical ? '#ef4444' : '#22c55e' },
              { label:'Límite',    value: data.limit?.toLocaleString('es-AR'),     color: text },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ padding:'14px 16px', borderRadius:14, background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc', border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0', textAlign:'center' }}>
                <p style={{ margin:'0 0 4px', fontSize:11, color:sub, textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:700 }}>{label}</p>
                <p style={{ margin:0, fontSize:20, fontWeight:900, color }}>{value}</p>
              </div>
            ))}
          </div>
          {data.resetDate && (
            <p style={{ margin:'16px 0 0', fontSize:12, color:sub, textAlign:'center' }}>
              🔄 Próximo reset: <strong style={{ color:text }}>{new Date(data.resetDate).toLocaleDateString('es-AR', { day:'numeric', month:'long', year:'numeric' })}</strong>
            </p>
          )}
        </div>
      </div>

      {(data.alert || data.critical) && (
        <div style={{ padding:'16px 20px', borderRadius:16, background: data.critical ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)', border:`1px solid ${data.critical ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}` }}>
          <p style={{ margin:0, fontWeight:700, fontSize:14, color: data.critical ? '#ef4444' : '#d97706' }}>
            {data.critical ? t('el_critical_msg') : t('el_alert_msg')}
          </p>
        </div>
      )}

      <a href="https://elevenlabs.io/app/subscription" target="_blank" rel="noopener noreferrer"
        style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'10px 16px', borderRadius:12, background: darkMode?'rgba(139,92,246,0.1)':'rgba(139,92,246,0.06)', border:'1px solid rgba(139,92,246,0.25)', color:'#8b5cf6', textDecoration:'none', fontSize:13, fontWeight:700 }}>
        Abrir dashboard ElevenLabs
      </a>

      <div style={{ borderRadius:20, background: darkMode?'rgba(255,255,255,0.04)':'white', border: darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid #e2e8f0' }}>
        <div style={{ padding:'20px 24px', borderBottom: darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid #f1f5f9', display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:28 }}>🤖</span>
          <div>
            <p style={{ margin:0, fontWeight:800, fontSize:16, color:text }}>Anthropic Claude</p>
            <p style={{ margin:0, fontSize:12, color:sub }}>Escaneo de checklists y Emergency Trainer</p>
          </div>
        </div>
        <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:12 }}>
          <p style={{ margin:0, fontSize:13, color:sub, lineHeight:1.6 }}>Claude se usa para escanear imagenes de checklists y generar procedimientos de emergencia. El uso se monitorea desde el dashboard de Anthropic.</p>
          <a href="https://console.anthropic.com/settings/usage" target="_blank" rel="noopener noreferrer"
            style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'10px 16px', borderRadius:12, background: darkMode?'rgba(37,99,235,0.1)':'rgba(37,99,235,0.06)', border:'1px solid rgba(37,99,235,0.25)', color:'#2563eb', textDecoration:'none', fontSize:13, fontWeight:700, alignSelf:'flex-start' }}>
            Abrir dashboard Anthropic
          </a>
        </div>
      </div>

      <CloudinaryMonitor darkMode={darkMode} />

      <div style={{ borderRadius:20, background: darkMode?'rgba(255,255,255,0.04)':'white', border: darkMode?'1px solid rgba(255,255,255,0.08)':'1px solid #e2e8f0' }}>
        <div style={{ padding:'20px 24px', borderBottom: darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid #f1f5f9', display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:28 }}>📧</span>
          <div>
            <p style={{ margin:0, fontWeight:800, fontSize:16, color:text }}>Resend</p>
            <p style={{ margin:0, fontSize:12, color:sub }}>Envio de emails transaccionales</p>
          </div>
        </div>
        <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:12 }}>
          <p style={{ margin:0, fontSize:13, color:sub, lineHeight:1.6 }}>Resend se usa para enviar emails de: recupero de contraseña, notificaciones de turnos, alertas de mantenimiento y confirmaciones de registro.</p>
          <a href="https://resend.com/emails" target="_blank" rel="noopener noreferrer"
            style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'10px 16px', borderRadius:12, background: darkMode?'rgba(0,188,212,0.1)':'rgba(0,188,212,0.06)', border:'1px solid rgba(0,188,212,0.25)', color:'#0891b2', textDecoration:'none', fontSize:13, fontWeight:700, alignSelf:'flex-start' }}>
            Abrir dashboard Resend
          </a>
        </div>
      </div>
    </div>
  );
};

// ── PriceCard — FUERA de TabConfig ───────────────────────────────
const PriceCard = ({ planLabel, planColor, usdKey, arsKey, prices, setPrices, darkMode }) => {
  const subtext = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const text    = darkMode ? 'rgba(255,255,255,0.9)'  : '#0f172a';
  const fieldStyle = {
    width:'100%', padding:'12px 16px 12px 28px', borderRadius:14, fontSize:15, fontWeight:700,
    border: darkMode ? '1.5px solid rgba(255,255,255,0.1)' : '1.5px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.06)' : '#f8fafc',
    color: darkMode ? 'white' : '#0f172a',
    outline:'none', fontFamily:'inherit', boxSizing:'border-box',
  };
  return (
    <div style={{ borderRadius:20, padding:20, border:`1.5px solid ${planColor}25`, background: darkMode ? `${planColor}08` : `${planColor}05` }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
        <div style={{ width:40, height:40, borderRadius:12, background:`${planColor}20`, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <DollarSign style={{ width:18, height:18, color:planColor }} />
        </div>
        <div>
          <p style={{ margin:0, fontSize:16, fontWeight:900, color:text }}>Plan {planLabel}</p>
          <p style={{ margin:0, fontSize:12, color:subtext }}>Precio mensual de suscripción</p>
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        {[
          { label:'Precio USD / mes', key:usdKey, step:'1' },
          { label:'Precio ARS / mes', key:arsKey, step:'100' },
        ].map(({ label, key, step }) => (
          <div key={key}>
            <label style={{ display:'block', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:subtext, marginBottom:6 }}>{label}</label>
            <div style={{ position:'relative' }}>
              <span style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', fontSize:15, fontWeight:800, color:planColor, pointerEvents:'none' }}>$</span>
              <input type="number" min="0" step={step} value={prices[key]} onChange={e => setPrices(p => ({ ...p, [key]: e.target.value }))} style={fieldStyle} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Tab: Configuración ────────────────────────────────────────────
const TabConfig = ({ darkMode }) => {
  const { t } = useTranslation('admin');
  const text    = darkMode ? 'rgba(255,255,255,0.9)'  : '#0f172a';
  const subtext = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';

  const [prices, setPrices]   = useState({
    price_basic_usd:'', price_basic_ars:'',
    price_pro_usd:'', price_pro_ars:'',
    price_enterprise_usd:'', price_enterprise_ars:'',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    api.get('/api/settings')
      .then(r => {
        const d = r.data.data || {};
        setPrices({
          price_basic_usd:      d.price_basic_usd      || '30',
          price_basic_ars:      d.price_basic_ars      || '30000',
          price_pro_usd:        d.price_pro_usd        || '80',
          price_pro_ars:        d.price_pro_ars        || '80000',
          price_enterprise_usd: d.price_enterprise_usd || '120',
          price_enterprise_ars: d.price_enterprise_ars || '120000',
        });
      })
      .catch(() => setError('Error al cargar configuración'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      await api.put('/api/settings', prices);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const cardStyle = {
    borderRadius:20, padding:20,
    border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.03)' : 'white',
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
      <div>
        <h2 style={{ margin:0, fontSize:26, fontWeight:900, color:text }}>{t('super_config_title')}</h2>
        <p style={{ margin:'4px 0 0', fontSize:14, color:subtext }}>{t('super_config_desc')}</p>
      </div>

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'64px 0' }}>
          <Loader style={{ width:32, height:32, animation:'spin 1s linear infinite', color:'#7c3aed' }} />
        </div>
      ) : (
        <>
          <div style={{ padding:'12px 16px', borderRadius:14, background: darkMode?'rgba(37,99,235,0.1)':'rgba(37,99,235,0.06)', border: darkMode?'1px solid rgba(37,99,235,0.2)':'1px solid rgba(37,99,235,0.15)', display:'flex', alignItems:'flex-start', gap:10 }}>
            <span style={{ fontSize:16, flexShrink:0 }}>💡</span>
            <div>
              <p style={{ margin:0, fontSize:13, fontWeight:700, color: darkMode?'#93c5fd':'#1d4ed8' }}>{t('super_config_realtime_title')}</p>
              <p style={{ margin:'2px 0 0', fontSize:12, color: darkMode?'rgba(147,197,253,0.7)':'#3b82f6', lineHeight:1.5 }}>{t('super_config_realtime_desc')}</p>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:14 }}>
            <PriceCard planLabel="Basic"      planColor="#64748b" usdKey="price_basic_usd"      arsKey="price_basic_ars"      prices={prices} setPrices={setPrices} darkMode={darkMode} />
            <PriceCard planLabel="Pro"        planColor="#2563eb" usdKey="price_pro_usd"        arsKey="price_pro_ars"        prices={prices} setPrices={setPrices} darkMode={darkMode} />
            <PriceCard planLabel="Enterprise" planColor="#7c3aed" usdKey="price_enterprise_usd" arsKey="price_enterprise_ars" prices={prices} setPrices={setPrices} darkMode={darkMode} />
          </div>

          <div style={cardStyle}>
            <p style={{ margin:'0 0 16px', fontSize:15, fontWeight:800, color:text }}>{t('super_config_plan_summary')}</p>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10 }}>
              {[
                { plan:'Trial',      color:'#f59e0b', usd:'0',                      features:['Checklists básicos','Sin soporte prioritario'] },
                { plan:'Basic',      color:'#64748b', usd:prices.price_basic_usd,   features:['Todo Trial','Hasta 5 aeronaves','Historial 30 días'] },
                { plan:'Pro',        color:'#2563eb', usd:prices.price_pro_usd,     features:['Todo Basic','Pre-aterrizaje','Firma digital','QR acceso'] },
                { plan:'Enterprise', color:'#7c3aed', usd:prices.price_enterprise_usd, features:['Todo Pro','Flow Mode','Emergency Trainer','ICAO English'] },
              ].map(({ plan, color, usd, features }) => (
                <div key={plan} style={{ padding:16, borderRadius:16, background:`${color}08`, border:`1.5px solid ${color}20` }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                    <div style={{ width:10, height:10, borderRadius:'50%', background:color, flexShrink:0 }} />
                    <p style={{ margin:0, fontSize:13, fontWeight:800, color:text }}>{plan}</p>
                  </div>
                  <p style={{ margin:'0 0 10px', fontSize:20, fontWeight:900, color, lineHeight:1 }}>
                    {usd === '0' ? 'Gratis' : `$${usd}`}
                    {usd !== '0' && <span style={{ fontSize:11, fontWeight:600, color:subtext, marginLeft:3 }}>USD/mes</span>}
                  </p>
                  <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                    {features.map(f => (
                      <div key={f} style={{ display:'flex', alignItems:'flex-start', gap:6 }}>
                        <span style={{ fontSize:10, color, flexShrink:0, marginTop:2 }}>✓</span>
                        <span style={{ fontSize:11, color:subtext, lineHeight:1.3 }}>{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div style={{ padding:'10px 14px', borderRadius:12, background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', display:'flex', alignItems:'center', gap:8 }}>
              <AlertCircle style={{ width:16, height:16, color:'#ef4444', flexShrink:0 }} />
              <p style={{ margin:0, fontSize:13, color:'#ef4444' }}>{error}</p>
            </div>
          )}

          <motion.button onClick={handleSave} disabled={saving}
            whileHover={!saving ? { scale:1.02, y:-1 } : {}} whileTap={!saving ? { scale:0.97 } : {}}
            style={{ width:'100%', padding:'16px', borderRadius:18, border:'none', cursor:saving?'not-allowed':'pointer', fontFamily:'inherit', fontWeight:800, fontSize:16, color:'white',
              background: saved ? '#16a34a' : 'linear-gradient(135deg,#475569,#334155)',
              display:'flex', alignItems:'center', justifyContent:'center', gap:10,
              boxShadow: saved ? '0 8px 28px rgba(22,163,74,0.3)' : '0 8px 28px rgba(71,85,105,0.3)', transition:'background 0.3s, box-shadow 0.3s' }}>
            {saving ? (
              <><Loader style={{ width:18, height:18, animation:'spin 1s linear infinite' }} /> {t('super_config_saving')}</>
            ) : saved ? (
              <><Check style={{ width:18, height:18 }} /> {t('super_config_saved')}</>
            ) : (
              <><Settings style={{ width:18, height:18 }} /> {t('super_config_save')}</>
            )}
          </motion.button>

          {saved && (
            <motion.div initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
              style={{ padding:'12px 16px', borderRadius:14, background: darkMode?'rgba(22,163,74,0.1)':'rgba(22,163,74,0.06)', border: darkMode?'1px solid rgba(22,163,74,0.2)':'1px solid rgba(22,163,74,0.15)', display:'flex', alignItems:'center', gap:8 }}>
              <CheckCircle style={{ width:16, height:16, color:'#16a34a', flexShrink:0 }} />
              <p style={{ margin:0, fontSize:13, fontWeight:600, color:'#16a34a' }}>{t('super_config_saved_msg')}</p>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
};

// ── Tab: Proveedores (vista global superadmin) ────────────────────

const TIPO_LABELS = {
  taller:'Taller/MRO', repuestos:'Repuestos', integral:'Integral',
  avionica:'Aviónica', motor:'Motor', aeroclub:'Aeroclub',
};

const TabProviders = ({ darkMode }) => {
  const [providers, setProviders]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [onlyPending, setOnlyPending] = useState(false);
  const [search, setSearch]           = useState('');
  const [editTarget, setEditTarget]   = useState(null);
  const [showEdit, setShowEdit]       = useState(false);
  const [acting, setActing]           = useState(null);

  const text   = darkMode ? 'rgba(255,255,255,0.9)'  : '#0f172a';
  const sub    = darkMode ? 'rgba(255,255,255,0.4)'  : '#64748b';
  const cardBg = darkMode ? 'rgba(255,255,255,0.03)' : 'white';
  const bdr    = darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid #e2e8f0';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (onlyPending) params.set('pending_corrections', 'true');
      if (search)      params.set('search', search);
      const res = await api.get(`/api/providers?${params}`);
      setProviders(res.data.data || []);
    } catch { setProviders([]); }
    finally { setLoading(false); }
  }, [onlyPending, search]);

  useEffect(() => { load(); }, [load]);

  const pendingCount = providers.filter(p => p.has_pending_correction).length;

  const handleVerify = async (id) => {
    setActing(id + '_v');
    try { await api.patch(`/api/providers/${id}/verify`); load(); }
    catch { } finally { setActing(null); }
  };

  const handleReview = async (id) => {
    setActing(id + '_r');
    try { await api.patch(`/api/providers/${id}/review`); load(); }
    catch { } finally { setActing(null); }
  };

  const handleSaveEdit = async () => {
    if (!editTarget?.nombre?.trim()) return;
    try {
      if (editTarget.id) await api.put(`/api/providers/${editTarget.id}`, editTarget);
      else               await api.post('/api/providers', editTarget);
      setShowEdit(false); setEditTarget(null); load();
    } catch { }
  };

  const inp = {
    width:'100%', padding:'9px 12px', borderRadius:10,
    background: darkMode?'rgba(255,255,255,0.06)':'#f8fafc',
    border: darkMode?'1.5px solid rgba(255,255,255,0.1)':'1.5px solid #e2e8f0',
    fontSize:13, color:text, outline:'none', fontFamily:'inherit', boxSizing:'border-box',
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
        <div>
          <h2 style={{ margin:'0 0 4px', fontSize:22, fontWeight:900, color:text }}>
            Directorio de Proveedores
          </h2>
          <p style={{ margin:0, fontSize:13, color:sub }}>
            {providers.length} proveedor{providers.length !== 1 ? 'es' : ''} — global
            {pendingCount > 0 && (
              <span style={{ marginLeft:8, fontWeight:700, color:'#f59e0b' }}>
                · {pendingCount} con correcciones pendientes
              </span>
            )}
          </p>
        </div>
        <motion.button
          onClick={() => {
            setEditTarget({ nombre:'', tipo:'taller', ubicacion_ciudad:'', ubicacion_provincia:'',
              telefono:'', email:'', sitio_web:'', habilitacion_anac:false, soporte_aog:false });
            setShowEdit(true);
          }}
          whileTap={{ scale:0.97 }}
          style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 18px', borderRadius:14,
            border:'none', cursor:'pointer', background:'linear-gradient(135deg,#7c3aed,#4f46e5)',
            color:'white', fontSize:13, fontWeight:700, fontFamily:'inherit' }}>
          <Plus style={{ width:15, height:15 }} /> Nuevo proveedor
        </motion.button>
      </div>

      {/* Filtros */}
      <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
        <div style={{ flex:1, minWidth:200, position:'relative' }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre, ciudad..."
            style={{ ...inp, paddingLeft:36 }} />
          <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:sub, fontSize:14, pointerEvents:'none' }}>🔍</span>
        </div>
        <motion.button onClick={() => setOnlyPending(v => !v)} whileTap={{ scale:0.97 }}
          style={{ padding:'9px 16px', borderRadius:12,
            border: onlyPending ? '1.5px solid rgba(245,158,11,0.4)' : bdr,
            background: onlyPending ? 'rgba(245,158,11,0.1)' : (darkMode?'rgba(255,255,255,0.04)':'#f8fafc'),
            color: onlyPending ? '#f59e0b' : sub, fontSize:13, fontWeight:700,
            cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6 }}>
          ⏳ Solo pendientes{pendingCount > 0 ? ` (${pendingCount})` : ''}
        </motion.button>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:sub }}>
          <Loader style={{ width:28, height:28, animation:'spin 1s linear infinite', margin:'0 auto 12px', display:'block' }} />
          <p style={{ margin:0, fontSize:13 }}>Cargando...</p>
        </div>
      ) : providers.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:sub }}>
          <p style={{ margin:0, fontSize:28 }}>📦</p>
          <p style={{ margin:'8px 0 0', fontSize:14 }}>
            {onlyPending ? 'No hay correcciones pendientes ✅' : 'No hay proveedores'}
          </p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {providers.map(p => (
            <div key={p.id} style={{
              borderRadius:16, background:cardBg, padding:'16px 20px',
              border: p.has_pending_correction
                ? '1.5px solid rgba(245,158,11,0.35)'
                : p.verified ? '1.5px solid rgba(37,99,235,0.2)' : bdr,
            }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  {/* Nombre + badges */}
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <p style={{ margin:0, fontSize:14, fontWeight:700, color:text }}>{p.nombre}</p>
                    <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20,
                      background:'rgba(100,116,139,0.1)', color:'#64748b' }}>
                      {TIPO_LABELS[p.tipo] || p.tipo}
                    </span>
                    {p.verified && (
                      <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20,
                        background:'rgba(37,99,235,0.1)', color:'#2563eb',
                        display:'inline-flex', alignItems:'center', gap:4 }}>
                        <ShieldCheck style={{ width:10, height:10 }} /> Verificado Condor360
                      </span>
                    )}
                    {p.habilitacion_anac && (
                      <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20,
                        background:'rgba(22,163,74,0.1)', color:'#16a34a' }}>ANAC ✓</span>
                    )}
                    {p.soporte_aog && (
                      <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20,
                        background:'rgba(239,68,68,0.1)', color:'#ef4444' }}>AOG 24h</span>
                    )}
                  </div>

                  {/* Datos de contacto */}
                  <div style={{ display:'flex', gap:12, marginTop:5, flexWrap:'wrap' }}>
                    {(p.ubicacion_ciudad || p.ubicacion_provincia) && (
                      <span style={{ fontSize:12, color:sub, display:'flex', alignItems:'center', gap:4 }}>
                        <MapPin style={{ width:11, height:11 }} />
                        {[p.ubicacion_ciudad, p.ubicacion_provincia].filter(Boolean).join(', ')}
                      </span>
                    )}
                    {p.telefono && <span style={{ fontSize:12, color:sub }}>📞 {p.telefono}</span>}
                    {p.email    && <span style={{ fontSize:12, color:sub }}>✉️ {p.email}</span>}
                    {p.sitio_web && (
                      <a href={p.sitio_web} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize:12, color:'#2563eb', textDecoration:'none' }}>
                        🌐 Sitio web
                      </a>
                    )}
                  </div>

                  {/* Badge corrección pendiente */}
                  {p.has_pending_correction && (
                    <div style={{ marginTop:10, padding:'8px 12px', borderRadius:10,
                      background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.2)',
                      display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ fontSize:14 }}>⏳</span>
                      <span style={{ fontSize:12, color:'#f59e0b', fontWeight:600, flex:1 }}>
                        Corrección sugerida por <strong>{p.correction_by}</strong>
                        {p.correction_at && ` · ${new Date(p.correction_at).toLocaleDateString('es-AR')}`}
                      </span>
                    </div>
                  )}
                </div>

                {/* Acciones */}
                <div style={{ display:'flex', gap:7, flexShrink:0, flexDirection:'column', alignItems:'flex-end' }}>
                  {!p.verified && (
                    <motion.button onClick={() => handleVerify(p.id)} disabled={!!acting}
                      whileTap={{ scale:0.97 }}
                      style={{ padding:'6px 12px', borderRadius:9, border:'none', cursor:'pointer',
                        background:'rgba(37,99,235,0.1)', color:'#2563eb', fontSize:11,
                        fontWeight:700, fontFamily:'inherit', display:'flex', alignItems:'center', gap:5,
                        opacity: acting === p.id + '_v' ? 0.6 : 1, whiteSpace:'nowrap' }}>
                      {acting === p.id + '_v'
                        ? <Loader style={{ width:11, height:11, animation:'spin 1s linear infinite' }} />
                        : <ShieldCheck style={{ width:11, height:11 }} />}
                      Verificar
                    </motion.button>
                  )}
                  {p.has_pending_correction && (
                    <motion.button onClick={() => handleReview(p.id)} disabled={!!acting}
                      whileTap={{ scale:0.97 }}
                      style={{ padding:'6px 12px', borderRadius:9, border:'none', cursor:'pointer',
                        background:'rgba(245,158,11,0.1)', color:'#f59e0b', fontSize:11,
                        fontWeight:700, fontFamily:'inherit', whiteSpace:'nowrap',
                        opacity: acting === p.id + '_r' ? 0.6 : 1 }}>
                      ✓ Revisado
                    </motion.button>
                  )}
                  <motion.button onClick={() => { setEditTarget({...p}); setShowEdit(true); }}
                    whileTap={{ scale:0.97 }}
                    style={{ padding:'6px 12px', borderRadius:9, border:bdr, background:'transparent',
                      color:sub, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
                    Editar
                  </motion.button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal editar / crear */}
      <AnimatePresence>
        {showEdit && editTarget && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(0,0,0,0.55)',
              backdropFilter:'blur(4px)', display:'flex', alignItems:'center',
              justifyContent:'center', padding:16 }}
            onClick={() => { setShowEdit(false); setEditTarget(null); }}>
            <motion.div initial={{ scale:0.95, y:16 }} animate={{ scale:1, y:0 }}
              onClick={e => e.stopPropagation()}
              style={{ width:'100%', maxWidth:480, borderRadius:20,
                background: darkMode?'#1e293b':'white', border:bdr,
                padding:24, maxHeight:'85vh', overflowY:'auto' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
                <p style={{ margin:0, fontSize:16, fontWeight:800, color:text }}>
                  {editTarget.id ? 'Editar proveedor' : 'Nuevo proveedor'}
                </p>
                <button onClick={() => { setShowEdit(false); setEditTarget(null); }}
                  style={{ background:'none', border:'none', cursor:'pointer', color:sub }}>
                  <X style={{ width:18, height:18 }} />
                </button>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {[
                  { key:'nombre',              label:'NOMBRE *', ph:'Nombre del proveedor', type:'text'  },
                  { key:'ubicacion_ciudad',    label:'CIUDAD',   ph:'Ciudad',               type:'text'  },
                  { key:'ubicacion_provincia', label:'PROVINCIA',ph:'Provincia',             type:'text'  },
                  { key:'telefono',            label:'TELÉFONO', ph:'+54 9 11 1234-5678',   type:'tel'   },
                  { key:'email',               label:'EMAIL',    ph:'contacto@proveedor.com',type:'email' },
                  { key:'sitio_web',           label:'SITIO WEB',ph:'https://...',           type:'text'  },
                  { key:'descripcion',         label:'DESCRIPCIÓN',ph:'Servicios que ofrece...', type:'textarea' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ fontSize:11, fontWeight:700, color:sub, display:'block', marginBottom:5 }}>{f.label}</label>
                    {f.type === 'textarea' ? (
                      <textarea value={editTarget[f.key] || ''} onChange={e=>setEditTarget(t=>({...t,[f.key]:e.target.value}))}
                        style={{ ...inp, resize:'vertical', minHeight:60 }} placeholder={f.ph} />
                    ) : (
                      <input value={editTarget[f.key] || ''} onChange={e=>setEditTarget(t=>({...t,[f.key]:e.target.value}))}
                        style={inp} placeholder={f.ph} type={f.type} />
                    )}
                  </div>
                ))}
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:sub, display:'block', marginBottom:5 }}>TIPO *</label>
                  <select value={editTarget.tipo || 'taller'} onChange={e=>setEditTarget(t=>({...t,tipo:e.target.value}))}
                    style={{ ...inp, cursor:'pointer' }}>
                    {Object.entries(TIPO_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div style={{ display:'flex', gap:20 }}>
                  <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:text }}>
                    <input type="checkbox" checked={!!editTarget.habilitacion_anac}
                      onChange={e=>setEditTarget(t=>({...t,habilitacion_anac:e.target.checked}))} />
                    Habilitación ANAC
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:text }}>
                    <input type="checkbox" checked={!!editTarget.soporte_aog}
                      onChange={e=>setEditTarget(t=>({...t,soporte_aog:e.target.checked}))} />
                    Soporte AOG 24h
                  </label>
                </div>
              </div>
              <div style={{ display:'flex', gap:10, marginTop:20 }}>
                <button onClick={() => { setShowEdit(false); setEditTarget(null); }}
                  style={{ flex:1, padding:'11px', borderRadius:12, border:bdr, background:'transparent',
                    color:sub, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                  Cancelar
                </button>
                <motion.button onClick={handleSaveEdit} disabled={!editTarget.nombre?.trim()}
                  whileTap={{ scale:0.97 }}
                  style={{ flex:2, padding:'11px', borderRadius:12, border:'none',
                    background:'linear-gradient(135deg,#7c3aed,#4f46e5)', color:'white',
                    fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
                  {editTarget.id ? 'Guardar cambios' : 'Crear proveedor'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

// ── SuperAdminPanel principal ─────────────────────────────────────
const SuperAdminPanel = ({ user, onLogout, darkMode, onToggleDark, onImpersonate }) => {
  const { t } = useTranslation('admin');

  const NAV_ITEMS = [
    { id: 'orgs',        label: t('nav_orgs'),        icon: Building2,  color: '#7c3aed' },
    { id: 'pending',     label: t('nav_pending'),     icon: Clock,      color: '#ef4444' },
    { id: 'metrics',     label: t('nav_metrics'),     icon: BarChart2,  color: '#2563eb' },
    { id: 'billing',     label: t('nav_billing'),     icon: CreditCard, color: '#16a34a' },
    { id: 'support',     label: t('nav_support'),     icon: Headphones, color: '#0891b2' },
    { id: 'flags',       label: t('nav_flags'),       icon: Flag,       color: '#f59e0b' },
    { id: 'maintenance', label: t('nav_maintenance'), icon: Bell,       color: '#dc2626' },
    { id: 'config',      label: t('nav_config'),      icon: Settings,   color: '#475569' },
    { id: 'elevenlabs',  label: t('nav_elevenlabs'),  icon: Mic,        color: '#8b5cf6' },
  ];

  const [activeTab, setActiveTab]           = useState('orgs');
  const [orgs, setOrgs]                     = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');
  const [showModal, setShowModal]           = useState(false);
  const [editOrg, setEditOrg]               = useState(null);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminModalOrg, setAdminModalOrg]   = useState(null);
  const [pendingCount, setPendingCount]     = useState(0);
  const [confirmModal, setConfirmModal]     = useState(null);
  const [deleteLoading, setDeleteLoading]   = useState(false);
  const [toggleModal, setToggleModal]       = useState(null);
  const [toggleLoading, setToggleLoading]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get('/api/organizations'); setOrgs(res.data.data || []); }
    catch { setError('Error al cargar organizaciones'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const fetchPending = async () => {
      try { const res = await api.get('/api/organizations/pending'); setPendingCount(res.data.count || 0); }
      catch { }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleSave = async (id, form, isEdit) => {
    if (isEdit) await api.put(`/api/organizations/${id}`, form);
    else await api.post('/api/organizations', form);
    await load();
  };

  const handleToggle = (org) => setToggleModal(org);
  const confirmToggleOrg = async () => {
    if (!toggleModal) return;
    setToggleLoading(true);
    try {
      await api.put(`/api/organizations/${toggleModal.id}`, { is_active: !toggleModal.is_active });
      await load(); setToggleModal(null);
    } catch { setError('Error al actualizar'); }
    finally { setToggleLoading(false); }
  };

  const handleDelete = (org) => setConfirmModal({ org, type: 'delete' });

  const handleDeleteConfirm = async () => {
    if (!confirmModal?.org) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/api/organizations/${confirmModal.org.id}`);
      await load(); setConfirmModal(null);
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al eliminar';
      if (msg.toLowerCase().includes('usuario')) {
        setConfirmModal({ org: confirmModal.org, type: 'has_users', message: msg });
      } else {
        setError(msg); setConfirmModal(null);
      }
    } finally { setDeleteLoading(false); }
  };

  const handleDeactivateInstead = async () => {
    if (!confirmModal?.org) return;
    setDeleteLoading(true);
    try {
      await api.put(`/api/organizations/${confirmModal.org.id}`, { is_active: false });
      await load(); setConfirmModal(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al desactivar'); setConfirmModal(null);
    } finally { setDeleteLoading(false); }
  };

  const handleFlagsUpdate = (orgId, features) => {
    setOrgs(prev => prev.map(o => o.id === orgId ? { ...o, features } : o));
  };

  const text = darkMode ? 'text-white' : 'text-gray-900';

  return (
    <div className={`flex h-screen overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* ── Sidebar ── */}
      <aside className={`hidden lg:flex flex-col w-64 flex-shrink-0 border-r ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className={`px-6 py-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:40, height:40, borderRadius:14, background:'linear-gradient(135deg,#7c3aed,#4f46e5)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Shield style={{ width:20, height:20, color:'white' }} />
            </div>
            <p className={`font-black text-lg ${text}`}>Condor360</p>
          </div>
        </div>
        <div className={`px-4 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          <div className={`flex items-center gap-3 p-3 rounded-2xl ${darkMode ? 'bg-gray-700/50' : 'bg-purple-50'}`}>
            <div style={{ width:36, height:36, borderRadius:12, background:'linear-gradient(135deg,#7c3aed,#4f46e5)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:800, fontSize:16 }}>
              {user?.name?.charAt(0).toUpperCase() || 'S'}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <p className={`font-semibold text-sm truncate ${text}`}>{user?.name}</p>
              <span style={{ fontSize:11, fontWeight:700, color:'#7c3aed', background:'rgba(124,58,237,0.1)', padding:'1px 8px', borderRadius:20 }}>{t('super_superadmin_label')}</span>
            </div>
          </div>
        </div>
        <nav style={{ flex:1, padding:12, overflowY:'auto' }}>
          <p style={{ margin:'4px 8px 8px', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', color:darkMode?'rgba(255,255,255,0.25)':'rgba(0,0,0,0.3)' }}>
            {t('super_sections')}
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
            {NAV_ITEMS.map(item => {
              const isActive = activeTab === item.id;
              return (
                <motion.button key={item.id} onClick={() => setActiveTab(item.id)}
                  whileHover={{ x: isActive ? 0 : 4 }} whileTap={{ scale:0.98 }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'11px 14px', borderRadius:16, border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:14, fontWeight:600, textAlign:'left', transition:'all 0.15s',
                    background: isActive ? `linear-gradient(135deg,${item.color},${item.color}dd)` : 'transparent',
                    color: isActive ? 'white' : darkMode ? 'rgba(156,163,175,1)' : 'rgba(75,85,99,1)' }}>
                  <item.icon style={{ width:18, height:18, flexShrink:0 }} />
                  <span style={{ flex:1 }}>{item.label}</span>
                  {item.id === 'pending' && pendingCount > 0 && (
                    <motion.span animate={{ scale:[1,1.15,1] }} transition={{ duration:1.5, repeat:Infinity }}
                      style={{ minWidth:20, height:20, borderRadius:10, background:'#ef4444', color:'white', fontSize:11, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 5px' }}>
                      {pendingCount}
                    </motion.span>
                  )}
                </motion.button>
              );
            })}
          </div>
        </nav>
        <div className={`px-3 py-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'} space-y-1`}>
          <button onClick={onToggleDark}
            style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:14, border:'none', cursor:'pointer', background:'transparent', fontFamily:'inherit', fontSize:13, fontWeight:500, color:darkMode?'rgba(156,163,175,1)':'rgba(75,85,99,1)' }}>
            {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {darkMode ? t('super_light_mode') : t('super_dark_mode')}
          </button>
          <button onClick={onLogout}
            style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:14, border:'none', cursor:'pointer', background:'transparent', fontFamily:'inherit', fontSize:13, fontWeight:500, color:'#ef4444' }}>
            <LogOut className="w-4 h-4" /> {t('super_logout')}
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <main style={{ flex:1, overflowY:'auto', padding:32 }}>
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-12 }} transition={{ duration:0.18 }}>
            {activeTab === 'orgs' && (
              <TabOrgs orgs={orgs} loading={loading} error={error} setError={setError}
                onEdit={(org) => { setEditOrg(org); setShowModal(true); }}
                onToggle={handleToggle} onDelete={handleDelete}
                onNew={() => { setEditOrg(null); setShowModal(true); }}
                onCreateAdmin={(org) => { setAdminModalOrg(org); setShowAdminModal(true); }}
                onImpersonate={(org) => onImpersonate && onImpersonate(org)}
                darkMode={darkMode} />
            )}
            {activeTab === 'pending'     && <TabPending darkMode={darkMode} onOrgApproved={() => { load(); setPendingCount(c => Math.max(0, c-1)); }} />}
            {activeTab === 'metrics'     && <TabMetrics darkMode={darkMode} />}
            {activeTab === 'billing'     && <TabBilling orgs={orgs} darkMode={darkMode} />}
            {activeTab === 'support'     && <TabSupport orgs={orgs} onImpersonate={(org) => onImpersonate && onImpersonate(org)} darkMode={darkMode} />}
            {activeTab === 'flags'       && <TabFlags orgs={orgs} onFlagsUpdate={handleFlagsUpdate} darkMode={darkMode} />}
            {activeTab === 'providers'   && <TabProviders darkMode={darkMode} />}
	    {activeTab === 'maintenance' && <TabMaintenance orgs={orgs} darkMode={darkMode} />}
            {activeTab === 'config'      && <TabConfig darkMode={darkMode} />}
            {activeTab === 'elevenlabs'  && <TabElevenLabs darkMode={darkMode} />}
          </motion.div>
        </AnimatePresence>
      </main>

      <AnimatePresence>
        {showModal && (
          <OrgModal org={editOrg} onSave={handleSave}
            onClose={() => { setShowModal(false); setEditOrg(null); }} darkMode={darkMode} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showAdminModal && adminModalOrg && (
          <CreateAdminModal org={adminModalOrg}
            onClose={() => { setShowAdminModal(false); setAdminModalOrg(null); }} darkMode={darkMode} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {confirmModal?.type === 'delete' && (
          <GeneralConfirmModal title={t('super_delete_org_title')}
            message={t('super_delete_org_msg', { name: confirmModal.org.name })}
            confirmLabel="Eliminar" icon={Trash2}
            onConfirm={handleDeleteConfirm} onCancel={() => setConfirmModal(null)}
            isLoading={deleteLoading} darkMode={darkMode} />
        )}
        {confirmModal?.type === 'has_users' && (
          <GeneralConfirmModal title={t('super_cannot_delete_title')}
            message={t('super_cannot_delete_msg', { name: confirmModal.org.name })}
            detail={t('super_cannot_delete_detail')}
            confirmLabel={t('super_deactivate_instead')}
            confirmColor="linear-gradient(135deg,#f59e0b,#d97706)"
            icon={Trash2}
            onConfirm={handleDeactivateInstead} onCancel={() => setConfirmModal(null)}
            isLoading={deleteLoading} darkMode={darkMode} />
        )}
        {toggleModal && (
          <GeneralConfirmModal
            title={`${toggleModal.is_active ? t('deactivate_user') : t('activate_user')} org`}
            message={`¿${toggleModal.is_active ? 'Desactivar' : 'Activar'} "${toggleModal.name}"${toggleModal.icao_code ? ` (${toggleModal.icao_code})` : ''}?`}
            detail={toggleModal.is_active ? t('toggle_org_deactivate_detail') : undefined}
            confirmLabel={toggleModal.is_active ? 'Desactivar' : 'Activar'}
            confirmColor={toggleModal.is_active ? '#ef4444' : '#22c55e'}
            icon={Trash2}
            onConfirm={confirmToggleOrg} onCancel={() => setToggleModal(null)}
            isLoading={toggleLoading} darkMode={darkMode} />
        )}
      </AnimatePresence>
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

export default SuperAdminPanel;
